import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import SessionDialog from '../sessions/components/SessionDialog'

const Faq = styled.details`
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 14px;
  padding: 12px 16px;
  & + & { margin-top: 8px; }
  > summary {
    cursor: pointer;
    list-style: none;
    font-weight: 600;
    color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  }
  > summary::-webkit-details-marker { display: none; }
  > p {
    margin: 8px 0 0;
    line-height: 1.6;
    color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
    font-size: 14px;
  }
`

const FAQS = [
  {
    q: 'How do I switch between family profiles?',
    a: 'Tap the avatar in the top-right and pick a profile. Everything you see — sessions, medicines, records — switches with you.',
  },
  {
    q: 'Can my therapist see what I log?',
    a: 'Yes — your active practitioners see your vitals, mood, medicine adherence and meal log. Documents you upload stay private until you tap Share on them.',
  },
  {
    q: 'How do I cancel a session?',
    a: 'Open Sessions → Upcoming, tap the session, and tap Cancel. The practitioner is notified automatically.',
  },
  {
    q: 'I\'m in crisis — what do I do?',
    a: 'Tap "Crisis support" in the left rail (or here on Help). It surfaces helplines, emergency numbers and the fastest way to reach your practitioner.',
  },
]

export default function HelpIndexPage() {
  const navigate = useNavigate()
  const [ticketOpen, setTicketOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)

  async function submitTicket() {
    setBusy(true)
    try {
      // Placeholder — backend B6 endpoint pending. We just simulate.
      await new Promise((r) => setTimeout(r, 300))
      window.alert('Ticket received — support will be in touch within 24h.')
      setSubject('')
      setBody('')
      setTicketOpen(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <div>
          <Pill>Help</Pill>
          <PageHeading style={{ marginTop: 12 }}>Help &amp; support</PageHeading>
          <PageSubheading>FAQs, raise a ticket, or get crisis support.</PageSubheading>
        </div>

        <Card
          as="button"
          onClick={() => navigate('/patient-home/help/crisis')}
          style={{
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'inherit',
            background: 'rgba(194, 77, 73, 0.08)',
            borderColor: '#C24D49',
          }}
        >
          <Row $between>
            <Row $gap="14px">
              <span style={{ fontSize: 24 }}>🆘</span>
              <CardTitle style={{ margin: 0, color: '#C24D49' }}>Crisis support</CardTitle>
            </Row>
            <Pill $tone="urgent">Always available</Pill>
          </Row>
          <Muted style={{ marginTop: 8 }}>
            If you&apos;re in a crisis right now, tap here for immediate help.
          </Muted>
        </Card>

        <Card>
          <CardTitle style={{ marginBottom: 12 }}>FAQs</CardTitle>
          {FAQS.map((f) => (
            <Faq key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </Faq>
          ))}
        </Card>

        <Card>
          <Row $between>
            <CardTitle style={{ margin: 0 }}>Still need help?</CardTitle>
            <GhostButton onClick={() => setTicketOpen(true)}>Raise a ticket</GhostButton>
          </Row>
          <Muted style={{ marginTop: 8 }}>
            Support replies within 24h on weekdays. For urgent care, message your practitioner directly.
          </Muted>
        </Card>
      </Stack>

      <SessionDialog
        open={ticketOpen}
        title="Raise a support ticket"
        subtitle="We'll respond within 24h on weekdays."
        primaryLabel="Send"
        primaryDisabled={!subject.trim() || !body.trim()}
        primaryLoading={busy}
        onPrimary={submitTicket}
        onClose={() => setTicketOpen(false)}
      >
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #E6E0D6',
            marginBottom: 10,
            fontFamily: 'inherit',
            fontSize: 14,
          }}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Describe what's going on"
          style={{
            width: '100%',
            minHeight: 100,
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #E6E0D6',
            fontFamily: 'inherit',
            fontSize: 14,
            resize: 'vertical',
          }}
        />
      </SessionDialog>
    </ContentMaxWidth>
  )
}
